import { useState } from 'react'
import { toast } from 'react-hot-toast'

import { AwxConnectedAccountType } from '@ankor-io/common/types/awx'
import { Button } from '@ankor-io/ui-components/src/Button'
import { Field, Label } from '@ankor-io/ui-components/src/Fieldset'
import { Input } from '@ankor-io/ui-components/src/Input'

import { useCreateVesselMutation } from '@/api/queries/useVesselQuery'
import { useUserContext } from '@/providers/user/useUserContext'

type VesselSetupProps = {
  onSave: (vesselId: string) => void
}

export const VesselSetup = ({ onSave }: VesselSetupProps) => {
  const [vesselName, setVesselName] = useState('')
  const createVesselMutation = useCreateVesselMutation()
  const { activeOrganizationId } = useUserContext()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!activeOrganizationId) {
      toast.error('Something went wrong. No organization found.')
      return
    }
    const createVessel = async () => {
      const vessel = await createVesselMutation.mutateAsync({
        name: vesselName.trim(),
        type: AwxConnectedAccountType.OPERATIONS,
      })
      onSave(vessel.id)
    }
    toast.promise(createVessel(), {
      loading: 'Creating vessel...',
      success: 'Vessel created',
      error: 'Failed to create vessel',
    })
  }

  return (
    <div className="space-y-6 sm:space-y-8">
      <div className="space-y-3 sm:space-y-4">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-50">Create your first vessel</h1>
        <p className="text-base sm:text-lg text-gray-500 dark:text-gray-400">
          Set up your vessel to start managing operations and APA, inviting users, and issuing cards.
        </p>
      </div>

      <form
        onSubmit={handleSubmit}
        className="space-y-4 sm:space-y-6"
      >
        <Field>
          <Label
            htmlFor="vessel-name"
            className="font-medium text-sm sm:text-base text-gray-900 dark:text-gray-50"
          >
            Vessel Name
          </Label>
          <Input
            id="vessel-name"
            type="text"
            placeholder="Enter vessel name"
            value={vesselName}
            onChange={(e) => setVesselName(e.target.value)}
            className="disabled:cursor-not-allowed disabled:opacity-50"
            autoFocus
            disabled={createVesselMutation.isPending}
          />
        </Field>

        <Button
          type="submit"
          color="primary"
          className="w-full disabled:cursor-not-allowed disabled:opacity-50"
          disabled={!vesselName.trim() || createVesselMutation.isPending}
        >
          {createVesselMutation.isPending ? 'Creating...' : 'Continue'}
        </Button>
      </form>
    </div>
  )
}

export default VesselSetup
